/**
 * 2026-09-23 (D-239) — operator "Satıştan çek" / "Yeniden etkinleştir" for
 * ALIAS-MODEL parents (gmx + mail.com, see OFF_SALE_PROVIDERS). Page-local
 * data layer (same pattern as
 * pages/mailbox-unit/mailboxUnitApi.ts). Every route nests under the existing
 * /admin/providers/:id/parents ingress prefix:
 *
 *   POST .../parents/:accountId/take-off-sale   {reason}            one parent
 *   POST .../parents/bulk-take-off-sale         {accountIds,reason} selected rows
 *   POST .../parents/:accountId/reactivate      {reason}            (endpoints.ts
 *        postProviderParentReactivate — phone wall, operator_off_sale, imap_rejected)
 *
 * Server semantics (apps/api/src/routes/admin/admin-provider-routes-generic.ts):
 * the parent becomes status='disabled' + status_detail='operator_off_sale';
 * aliases are NOT touched; a parent holding a live order (reserved/leased
 * alias) is skipped; an already-disabled parent is skipped; one audit row per
 * request. The UI never decides any of this — it only reports what the server
 * returned.
 */
import { callAdminApi, type ApiClientOptions, type ApiResult } from '../../api/client.js';
import { freshIdempotencyKey } from '../../api/idempotency-key.js';

/**
 * Alias-model providers the server's take-off-sale path serves — mirrors
 * DEFAULT_ALIAS_MODEL_OFF_SALE_PROVIDERS in
 * apps/api/src/routes/admin/admin-provider-routes-generic.ts. mail.com joined
 * on 2026-09-23 together with the server: its own renewal buttons (Canlandır,
 * Toplu Yenileme FORCE, Force Renew, Cooldown Sıfırla, Aliasları Sil & Renew)
 * now skip or refuse (409 reactivate_required) an off-sale parent server-side,
 * so they no longer put it back on sale. Keep this set equal to the server's.
 */
const OFF_SALE_PROVIDERS: ReadonlySet<string> = new Set(['gmx', 'mailcom']);

export function supportsOperatorOffSale(providerId: string): boolean {
  return OFF_SALE_PROVIDERS.has(providerId);
}

/** Disabled kinds that only "Yeniden etkinleştir" brings back. */
export type ReactivateKind = 'phone_wall' | 'operator_off_sale' | 'imap_rejected';

export function reactivateKindOf(account: {
  readonly status: string;
  readonly statusDetail?: string | null;
}): ReactivateKind | null {
  if (account.status !== 'disabled') return null;
  switch (account.statusDetail) {
    case 'phone_verification_required':
      return 'phone_wall';
    case 'operator_off_sale':
      return 'operator_off_sale';
    case 'imap_rejected':
      return 'imap_rejected';
    default:
      return null;
  }
}

export interface ParentTakeOffSaleResponse {
  readonly ok: true;
  readonly accountId: string;
  readonly status: 'disabled';
  readonly statusDetail: 'operator_off_sale';
}

export interface ParentsBulkTakeOffSaleResponse {
  readonly requested: number;
  readonly disabled: number;
  readonly skippedInFlight: number;
  readonly skippedAlreadyDisabled: number;
  readonly notFound: number;
  readonly disabledIds: readonly string[];
  readonly skippedInFlightIds: readonly string[];
  readonly alreadyDisabledIds: readonly string[];
  readonly notFoundIds: readonly string[];
}

/** Envelope codes whose specific cause the server puts in `detail`. */
const GENERIC_ERROR_CODES: ReadonlySet<string> = new Set([
  'bad_request',
  'not_found',
  'service_unavailable',
]);

/**
 * These routes answer `{error:'<code>'}` (or `{error, detail}`) with no
 * `reason`, which the shared client flattens to `http_<status>`. Surface the
 * server code so the operator sees e.g. `parent_in_flight`.
 */
function liftErrorCode<T>(res: ApiResult<T>): ApiResult<T> {
  if (res.kind !== 'error' || res.reason !== `http_${res.status}`) return res;
  const error = res.body?.['error'];
  const detail = res.body?.['detail'];
  if (typeof error === 'string' && GENERIC_ERROR_CODES.has(error) && typeof detail === 'string' && detail) {
    return { ...res, reason: detail };
  }
  if (typeof error === 'string' && error.length > 0) return { ...res, reason: error };
  return res;
}

export async function postParentTakeOffSale(
  options: ApiClientOptions,
  providerId: string,
  accountId: string,
  reason: string,
): Promise<ApiResult<ParentTakeOffSaleResponse>> {
  return liftErrorCode(
    await callAdminApi<ParentTakeOffSaleResponse>(options, {
      method: 'POST',
      path: `/admin/providers/${encodeURIComponent(providerId)}/parents/${encodeURIComponent(accountId)}/take-off-sale`,
      body: { reason },
      idempotencyKey: freshIdempotencyKey(`${providerId}-take-off-sale-${accountId}`),
    }),
  );
}

export async function postParentsBulkTakeOffSale(
  options: ApiClientOptions,
  providerId: string,
  accountIds: readonly string[],
  reason: string,
): Promise<ApiResult<ParentsBulkTakeOffSaleResponse>> {
  return liftErrorCode(
    await callAdminApi<ParentsBulkTakeOffSaleResponse>(options, {
      method: 'POST',
      path: `/admin/providers/${encodeURIComponent(providerId)}/parents/bulk-take-off-sale`,
      body: { accountIds, reason },
      idempotencyKey: freshIdempotencyKey(`${providerId}-bulk-take-off-sale`),
    }),
  );
}

/** Turkish text for the server's refusal codes (unknown codes pass through). */
const TAKE_OFF_SALE_ERROR_TEXT: Record<string, string> = {
  parent_in_flight: 'Parent\'ın aliaslarından biri aktif bir siparişte; sipariş kapanınca tekrar deneyin.',
  already_disabled: 'Parent zaten devre dışı.',
  account_not_found: 'Parent bulunamadı.',
  parent_locked_retry: 'Parent şu an başka bir işlem tarafından kilitli; birkaç saniye sonra tekrar deneyin.',
  reason_required: 'Neden en az 4 karakter olmalı.',
  forbidden: 'Bu işlem için provider:manage_inventory yetkisi gerekiyor.',
};

export function describeTakeOffSaleError(code: string): string {
  return TAKE_OFF_SALE_ERROR_TEXT[code] ?? code;
}
